import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatRelativeTime } from '@/lib/formatters';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuthenticatedFetch } from '@/hooks/useAuthenticatedFetch';
import { cn } from '@/lib/utils';
import { getCachedData, setCachedData } from '@/lib/cache';

const PAGE_SIZE = 100;

type SortKey = 'createdAt' | 'email' | 'fullName';
type SortDirection = 'asc' | 'desc';

type AdminUser = {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  createdAt?: string | null;
  createdAtDateString?: string | null;
  status?: string | null;
  lastActiveAt?: string | null;
  tag?: string | null;
};

type UsersResponse = {
  users?: AdminUser[];
  data?: AdminUser[];
  total?: number;
  totalCount?: number;
  lastKey?: string;
  hasMore?: boolean;
};

export default function UsersListPage() {
  const authenticatedFetch = useAuthenticatedFetch();
  // Navigation history: array of lastKeys for each page
  // Index 0 = first page (no key), Index 1 = second page key, etc.
  const [navigationHistory, setNavigationHistory] = useState<(string | null)[]>(
    [null],
  );
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [nextPageKey, setNextPageKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; direction: SortDirection }>({
    key: 'createdAt',
    direction: 'desc',
  });
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
      // Reset pagination when search changes
      if (searchInput !== searchQuery) {
        setNavigationHistory([null]);
        setCurrentPageIndex(0);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput, searchQuery]);

  useEffect(() => {
    const controller = new AbortController();
    async function fetchUsers() {
      try {
        const currentLastKey = navigationHistory[currentPageIndex];

        // Check cache first
        const cacheKey = `users_${searchQuery}_${currentLastKey ?? 'first'}`;
        const cachedData = getCachedData<UsersResponse>(cacheKey);

        if (cachedData) {
          const list = cachedData.users ?? cachedData.data ?? [];
          setUsers(list);
          const total = cachedData.total ?? cachedData.totalCount ?? null;
          setTotalCount(typeof total === 'number' ? total : null);
          setNextPageKey(cachedData.lastKey ?? null);
          setLoading(false);
          return;
        }

        setLoading(true);
        setError(null);

        // Build URL with search and pagination
        let url = `users?limit=${PAGE_SIZE}`;
        if (searchQuery) {
          url += `&search=${encodeURIComponent(searchQuery)}`;
        }
        if (currentLastKey && !searchQuery) {
          url += `&lastKey=${encodeURIComponent(currentLastKey)}`;
        }

        const response = await authenticatedFetch(url, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch users (${response.status})`);
        }

        const payload = (await response.json()) as UsersResponse;
        if (controller.signal.aborted) {
          return;
        }

        // Store in cache
        setCachedData(cacheKey, payload);

        const list = payload.users ?? payload.data ?? [];
        setUsers(list);
        const total = payload.total ?? payload.totalCount ?? null;
        setTotalCount(typeof total === 'number' ? total : null);
        setNextPageKey(payload.lastKey ?? null);
      } catch (fetchError) {
        if (controller.signal.aborted) {
          return;
        }
        console.error(fetchError);
        setError(
          fetchError instanceof Error
            ? fetchError.message
            : 'Unable to fetch users.',
        );
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    fetchUsers();

    return () => controller.abort();
  }, [authenticatedFetch, currentPageIndex, navigationHistory, searchQuery]);

  const sortedUsers = useMemo(() => {
    const data = [...users];
    const { key, direction } = sort;
    const multiplier = direction === 'asc' ? 1 : -1;

    return data.sort((a, b) => {
      const aValue = getComparableValue(a, key);
      const bValue = getComparableValue(b, key);

      if (aValue < bValue) return -1 * multiplier;
      if (aValue > bValue) return 1 * multiplier;
      return 0;
    });
  }, [users, sort]);

  const totalPages = totalCount
    ? Math.max(Math.ceil(totalCount / PAGE_SIZE), 1)
    : null;
  const hasNextPage = !!nextPageKey;
  const hasPreviousPage = currentPageIndex > 0;

  const handleNextPage = () => {
    if (!nextPageKey) return;

    // Add the next page key to navigation history if it's not already there
    if (currentPageIndex === navigationHistory.length - 1) {
      setNavigationHistory([...navigationHistory, nextPageKey]);
    }
    setCurrentPageIndex(currentPageIndex + 1);
  };

  const handlePreviousPage = () => {
    if (currentPageIndex === 0) return;
    setCurrentPageIndex(currentPageIndex - 1);
  };

  const handleSort = (key: SortKey) => {
    setSort((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, direction: 'asc' };
    });
  };

  return (
    <div className="space-y-3 sm:space-y-6">
      <Breadcrumb
        items={[{ label: 'Dashboard', to: '/' }, { label: 'Users' }]}
      />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Creative AI Writer CRM
          </h1>
          <p className="text-muted-foreground">
            Manage users, subscriptions, and engagement
          </p>
        </div>
      </div>

      <div className="relative">
        <Input
          type="text"
          placeholder="Search by email or name..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="max-w-md"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Directory</CardTitle>
          <CardDescription>
            Showing {users.length} users on page {currentPageIndex + 1}
            {totalCount ? ` (${totalCount.toLocaleString()} total)` : ''}.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 sm:space-y-4">
          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>
                    <button
                      type="button"
                      onClick={() => handleSort('email')}
                      className={cn(
                        'flex items-center gap-1 text-left text-sm font-medium hover:underline',
                        sort.key === 'email'
                          ? 'text-foreground'
                          : 'text-muted-foreground',
                      )}
                    >
                      Email
                      <SortIndicator
                        active={sort.key === 'email'}
                        direction={sort.direction}
                      />
                    </button>
                  </TableHead>
                  <TableHead>
                    <button
                      type="button"
                      onClick={() => handleSort('fullName')}
                      className={cn(
                        'flex items-center gap-1 text-left text-sm font-medium hover:underline',
                        sort.key === 'fullName'
                          ? 'text-foreground'
                          : 'text-muted-foreground',
                      )}
                    >
                      Name
                      <SortIndicator
                        active={sort.key === 'fullName'}
                        direction={sort.direction}
                      />
                    </button>
                  </TableHead>
                  <TableHead>
                    <button
                      type="button"
                      onClick={() => handleSort('createdAt')}
                      className={cn(
                        'flex items-center gap-1 text-left text-sm font-medium hover:underline',
                        sort.key === 'createdAt'
                          ? 'text-foreground'
                          : 'text-muted-foreground',
                      )}
                    >
                      Created
                      <SortIndicator
                        active={sort.key === 'createdAt'}
                        direction={sort.direction}
                      />
                    </button>
                  </TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/users/${user.id}`}
                          className="font-mono font-medium text-primary hover:underline"
                        >
                          {user.id}
                        </Link>
                        {user.tag && (
                          <Badge variant="outline">{user.tag}</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{getDisplayName(user)}</TableCell>
                    <TableCell className="font-mono">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs text-muted-foreground">
                          {formatRelativeTime(
                            user.createdAt
                              ? new Date(user.createdAt).getTime()
                              : undefined,
                          )}
                        </span>
                        <span className="text-xs">
                          {user.createdAtDateString ?? '—'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {user.status ? (
                        <Badge variant="secondary">{user.status}</Badge>
                      ) : (
                        <Badge variant="outline">unknown</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableCaption>
                {loading && 'Loading users…'}
                {error && <span className="text-destructive">{error}</span>}
                {!loading &&
                  !error &&
                  sortedUsers.length === 0 &&
                  'No users found.'}
              </TableCaption>
            </Table>
          </div>
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={handlePreviousPage}
              disabled={!hasPreviousPage || loading}
            >
              Previous
            </Button>
            <div className="text-sm text-muted-foreground">
              Page {currentPageIndex + 1}
              {totalPages ? ` of ${totalPages}` : ''}
            </div>
            <Button
              variant="outline"
              onClick={handleNextPage}
              disabled={!hasNextPage || loading}
            >
              Next
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function getComparableValue(user: AdminUser, key: SortKey) {
  switch (key) {
    case 'email':
      return (user.email ?? '').toLowerCase();
    case 'fullName':
      return getDisplayName(user).toLowerCase();
    case 'createdAt':
    default:
      return user.createdAt ? new Date(user.createdAt).getTime() : 0;
  }
}

function getDisplayName(user: AdminUser) {
  if (user.fullName) {
    return user.fullName;
  }

  const names = [user.firstName, user.lastName].filter(Boolean);
  return names.length > 0 ? names.join(' ') : '—';
}

type SortIndicatorProps = {
  active: boolean;
  direction: SortDirection;
};

function SortIndicator({ active, direction }: SortIndicatorProps) {
  return (
    <span className="text-xs">
      {active ? (direction === 'asc' ? '▲' : '▼') : '△'}
    </span>
  );
}
